// src/components/Therapists.jsx
import React, { useEffect, useMemo, useState } from 'react';

import { API_BASE } from '../../config/runtime';

const Therapists = () => {
  const [selectedLocation, setSelectedLocation] = useState('ALL');
  const [therapists, setTherapists] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTherapist, setSelectedTherapist] = useState(null);

  // Fetch Therapists
  useEffect(() => {
    const fetchTherapists = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/get_therapists.php`);
        const data = await response.json();
        if (data.success) setTherapists(data.therapists || []);
      } catch {
        setTherapists([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTherapists();
  }, []);

  // Fetch Branches
  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await fetch(`${API_BASE}/get_branches.php`);
        const data = await response.json();
        if (data.success && Array.isArray(data.branches)) {
          setBranches(data.branches);
        }
      } catch {
        setBranches([]);
      }
    };
    fetchBranches();
  }, []);

  const locations = useMemo(() => {
    const branchNames = branches.map(b => b.branch_name).filter(Boolean);
    return ["ALL", ...branchNames];
  }, [branches]);

  const visibleTherapists = useMemo(() => {
    if (selectedLocation === 'ALL') return therapists;
    const selected = selectedLocation.trim().toLowerCase();
    return therapists.filter(
      (t) => String(t.branch_name || '').trim().toLowerCase() === selected
    );
  }, [selectedLocation, therapists]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (selectedTherapist) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [selectedTherapist]);

  return (
    <div className="py-12 md:py-20 bg-white min-h-screen">
      <div className="max-w-7xl mx-auto px-6">

        {/* Header Section */}
        <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-12 gap-8 pb-10 border-b border-gray-50">
          <h2 className="text-5xl md:text-6xl font-serif uppercase tracking-tighter text-gray-900 leading-none">
            Therapists
          </h2>

          {/* Location filter */}
          <div className="w-full md:w-auto">
            {/* Mobile dropdown */}
            <div className="block md:hidden">
              <select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                className="w-full px-5 py-3 rounded-full text-[11px] font-black uppercase tracking-[0.1em] border border-gray-200 bg-white text-gray-700 appearance-none focus:outline-none focus:border-black transition-all"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2.5'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 1.25rem center' }}
              >
                {locations.map((loc) => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </div>

            {/* Desktop pills - Cleaned & Balanced */}
            <div className="hidden md:flex flex-wrap justify-end gap-3 max-w-3xl">
              {locations.map((loc) => (
                <button
                  key={loc}
                  onClick={() => setSelectedLocation(loc)}
                  className={`px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-[0.12em] transition-all duration-300 border ${
                    selectedLocation === loc
                      ? 'bg-black text-white border-black shadow-lg shadow-gray-200 translate-y-[-1px]'
                      : 'bg-white text-gray-400 border-gray-100 hover:border-gray-900 hover:text-gray-900'
                  }`}
                >
                  {loc}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Dynamic Category Divider */}
        <div className="flex items-center justify-center mb-16">
          <div className="flex-1 h-px bg-gray-100"></div>
          <h3 className="px-8 text-[11px] font-black text-gray-400 uppercase tracking-[0.5em] text-center">
            {selectedLocation === 'ALL' ? 'Global Roster' : selectedLocation}
          </h3>
          <div className="flex-1 h-px bg-gray-100"></div>
        </div>

        {loading ? (
          <div className="flex justify-center py-24 text-[10px] font-bold text-gray-300 uppercase tracking-widest animate-pulse">
            Fetching Practitioners...
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-x-8 gap-y-16">
            {visibleTherapists.map((therapist) => (
              <TherapistCard
                key={therapist.therapist_id}
                therapist={therapist}
                onView={() => setSelectedTherapist(therapist)}
              />
            ))}
          </div>
        )}

        {!loading && visibleTherapists.length === 0 && (
          <div className="py-24 text-center">
            <p className="text-gray-400 font-serif italic text-sm tracking-widest uppercase opacity-60">
              No practitioners available for this branch.
            </p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selectedTherapist && (
        <TherapistModal
          therapist={selectedTherapist}
          onClose={() => setSelectedTherapist(null)}
        />
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────
   Therapist Card Component
───────────────────────────────────────────── */
const TherapistCard = ({ therapist, onView }) => (
  <div className="group flex flex-col items-center text-center cursor-pointer" onClick={onView}>
    <div className="relative w-full aspect-square mb-6">
      <div className="relative w-full h-full rounded-full p-[4px] bg-[radial-gradient(circle_at_top_left,#ffffff,#f1e8e2_55%,#eadfd7)] shadow-[0_15px_35px_rgba(28,18,14,0.1)] transition-all duration-500 group-hover:-translate-y-2 group-hover:shadow-[0_20px_40px_rgba(28,18,14,0.15)]">
        <div className="w-full h-full rounded-full p-[2px] bg-[linear-gradient(135deg,#e8d9cf,#ffffff_35%,#d8c2b5)]">
          <div className="w-full h-full rounded-full overflow-hidden border border-[#f2e7e0] bg-[#fdfaf7]">
            {therapist.image ? (
              <img
                src={therapist.image}
                alt={therapist.name}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[10px] text-gray-300 uppercase font-black italic">
                Hygea
              </div>
            )}
          </div>
        </div>
      </div>
    </div>

    <div className="flex flex-col items-center">
      <h4 className="text-[13px] font-black uppercase tracking-tight text-gray-900 group-hover:text-[#984e5c] transition-colors leading-tight">
        {therapist.name}
      </h4>
      <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5">
        {therapist.branch_name || 'Staff'}
      </p>
    </div>
  </div>
);

/* ─────────────────────────────────────────────
   Therapist Modal Component
───────────────────────────────────────────── */
const TherapistModal = ({ therapist, onClose }) => (
  <div
    className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-md p-0 sm:p-4 animate-in fade-in duration-300"
    onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
  >
    <div className="w-full sm:w-[400px] rounded-t-[3rem] sm:rounded-[3rem] bg-white shadow-2xl overflow-hidden animate-in slide-in-from-bottom sm:zoom-in duration-300 max-h-[92svh] flex flex-col">
      <div className="relative h-64 bg-gray-100 flex-shrink-0">
        {therapist.image && <img src={therapist.image} alt="" className="w-full h-full object-cover" />}
        <button onClick={onClose} className="absolute top-6 right-6 w-10 h-10 bg-black/20 backdrop-blur-md rounded-full text-white flex items-center justify-center hover:bg-black/40 transition-all">✕</button>
      </div>
      <div className="p-10 text-center overflow-y-auto flex-1">
        <span className="text-[10px] font-black text-[#984e5c] uppercase tracking-[0.2em]">{therapist.branch_name}</span>
        <h2 className="text-3xl font-black text-gray-900 uppercase mt-2 tracking-tighter">{therapist.name}</h2>
        <div className="h-px bg-gray-100 w-12 mx-auto my-6"></div>
        <p className="text-gray-500 text-sm italic mb-8 leading-relaxed">
          {therapist.bio?.trim() || 'A professional wellness practitioner dedicated to the Hygea philosophy.'}
        </p>
        <button onClick={onClose} className="w-full py-4 rounded-2xl bg-black text-white font-black uppercase text-[10px] tracking-[0.2em] hover:bg-[#984e5c] transition-all">
          Close Profile
        </button>
      </div>
    </div>
  </div>
);

export default Therapists;
